import type {AccessEntry, ShareableItem} from "@components/DPDialog/modals/DPShareModal/DPShareModal";
import WebAppServerClient from "@services/clients/WebAppServerClient";

export async function buildShareables(companyId: string): Promise<ShareableItem[]> {
  const [users, groups] = await Promise.all([
    WebAppServerClient.request(`/api/company/${companyId}/user`, "GET"),
    WebAppServerClient.listPermissionGroups(companyId)
  ]);
  const userItems: ShareableItem[] = (users ?? []).map((user: any) => ({
    id: user.email ?? user.id,
    name: `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || (user.email ?? user.id),
    email: user.email ?? user.id,
    type: "user"
  }));
  const groupItems: ShareableItem[] = (groups ?? []).map((group: any) => ({
    id: group.id,
    name: group.name,
    type: "group",
    icon: group.icon,
    memberCount: Array.isArray(group.memberIds) ? group.memberIds.length : 0
  }));
  return [...userItems, ...groupItems];
}

export function grantsToAccessList(grants: Record<string, any>, shareables: ShareableItem[], currentUserId?: string): AccessEntry[] {
  const byKey = new Map(shareables.map(item => [`${item.type}:${item.id}`, item]));
  const list: AccessEntry[] = [];
  for (const [subject, entry] of Object.entries(grants ?? {})) {
    if (entry?.effect !== "allow") { continue; }
    let type: "user" | "group";
    let id: string;
    if (subject.startsWith("user:")) { type = "user"; id = subject.slice(5); } else if (subject.startsWith("group:")) { type = "group"; id = subject.slice(6); } else { continue; }
    const item = byKey.get(`${type}:${id}`);
    const isOwner = type === "user" && !!currentUserId && id === currentUserId && entry.level === "edit";
    list.push({
      id,
      name: item?.name ?? id,
      email: item?.email,
      type,
      role: isOwner ? "owner" : (entry.level === "edit" ? "can_edit" : "can_view"),
      icon: item?.icon,
      memberCount: item?.memberCount
    });
  }
  return list;
}

export function accessListToGrants(accessList: AccessEntry[], originalGrants: Record<string, any>): Record<string, any> {
  const grants: Record<string, any> = {};
  for (const [subject, entry] of Object.entries(originalGrants ?? {})) {
    if (subject.startsWith("company:") || entry?.effect === "deny") { grants[subject] = entry; }
  }
  for (const entry of accessList) {
    const subject = entry.type === "group" ? `group:${entry.id}` : `user:${entry.id}`;
    grants[subject] = {level: entry.role === "can_view" ? "view" : "edit", effect: "allow"};
  }
  return grants;
}
